import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Download, Search, ChevronDown, ChevronRight, ShieldCheck, Send, RotateCcw } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { money, outletNames, formatDate } from "../../lib/format";
import { PanelHead, Badge, Modal } from "../../components/UI";
import { useOutlets } from "../../lib/useOutlets";
import { useAuth } from "../../context/AuthContext";

export function PoReceivedDetail() {
  const { user } = useAuth();
  const outlets = useOutlets();
  const canExport = ["admin", "finance"].includes(user?.role);
  const [filters, setFilters] = useState({
    start: "", end: "", vendor: "", po_number: "", document_number: "",
    account_code: "", outlet: "all", status: "", xero_status: "",
  });
  const [applied, setApplied] = useState({
    start: "", end: "", vendor: "", po_number: "", document_number: "",
    account_code: "", outlet: "all", status: "", xero_status: "",
  });
  const [data, setData] = useState({ rows: [], total: 0, page: 1, page_size: 25, grand_total: 0, pending_export: 0 });
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [selected, setSelected] = useState([]);
  const [validation, setValidation] = useState(null);
  const [page, setPage] = useState(1);
  const [selectingAll, setSelectingAll] = useState(false);

  const buildParams = (f) => {
    const params = {};
    Object.entries(f).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
    return params;
  };

  const load = () => {
    setLoading(true);
    const params = { ...buildParams(applied), page, page_size: 25 };
    api.get("/reports/po-received-detail", { params })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, applied]);

  // Apply = commit the live filter inputs; clear selection so it never spans filters.
  const applyFilters = () => { setSelected([]); setPage(1); setApplied(filters); };

  const toggleSel = (id) => setSelected((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const allOnPageSelected = data.rows.length > 0 && data.rows.every((r) => selected.includes(r.id));
  const allMatchingSelected = data.total > 0 && selected.length >= data.total;
  const toggleAll = () => {
    if (allOnPageSelected) setSelected((p) => p.filter((id) => !data.rows.some((r) => r.id === id)));
    else setSelected((p) => [...new Set([...p, ...data.rows.map((r) => r.id)])]);
  };
  const selectAllMatching = async () => {
    setSelectingAll(true);
    try {
      // Use the SAME applied filters that produced the visible list, so the
      // selected count always matches the displayed total (all pages).
      const { data: res } = await api.get("/reports/po-received-detail/ids", { params: buildParams(applied) });
      setSelected(res.ids);
      toast.success(`${res.ids.length} transaksi terpilih (semua halaman)`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSelectingAll(false);
    }
  };

  const runValidate = async () => {
    if (selected.length === 0) return toast.error("Select at least one GRN");
    try {
      const { data: res } = await api.post("/reports/xero-bills/validate", { grn_ids: selected });
      setValidation(res);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const runExport = async () => {
    if (selected.length === 0) return toast.error("Select at least one GRN");
    try {
      const res = await api.post("/reports/xero-bills/export", { grn_ids: selected }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "xero_bills.csv"; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Xero Bills CSV exported. Valid GRNs marked as exported.");
      setValidation(null);
      setSelected([]);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const markUnexported = async () => {
    if (selected.length === 0) return toast.error("Select at least one GRN");
    if (!window.confirm("Mark selected GRNs as NOT exported to Xero?")) return;
    try {
      const { data: res } = await api.post("/reports/xero-bills/mark-unexported", { grn_ids: selected });
      toast.success(`${res.updated} GRN(s) marked un-exported`);
      setSelected([]);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / data.page_size));
  const inpStyle = { border: "1px solid var(--line)", borderRadius: 6, padding: "8px 10px", font: "12px 'DM Sans'", background: "#fff" };

  return (
    <div>
      <div className="report-bar" style={{ gap: 10 }}>
        <label className="field small"><span>From (received)</span><input data-testid="prd-start" type="date" value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })} style={inpStyle} /></label>
        <label className="field small"><span>To</span><input data-testid="prd-end" type="date" value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })} style={inpStyle} /></label>
        <label className="field small"><span>Vendor</span><input data-testid="prd-vendor" value={filters.vendor} onChange={(e) => setFilters({ ...filters, vendor: e.target.value })} placeholder="name" style={inpStyle} /></label>
        <label className="field small"><span>PO No.</span><input data-testid="prd-po" value={filters.po_number} onChange={(e) => setFilters({ ...filters, po_number: e.target.value })} style={inpStyle} /></label>
        <label className="field small"><span>Doc No.</span><input data-testid="prd-doc" value={filters.document_number} onChange={(e) => setFilters({ ...filters, document_number: e.target.value })} style={inpStyle} /></label>
        <label className="field small"><span>Account</span><input data-testid="prd-account" value={filters.account_code} onChange={(e) => setFilters({ ...filters, account_code: e.target.value })} placeholder="code" style={inpStyle} /></label>
        <label className="field small"><span>Outlet</span>
          <select data-testid="prd-outlet" value={filters.outlet} onChange={(e) => setFilters({ ...filters, outlet: e.target.value })} style={inpStyle}>
            <option value="all">All</option>
            {outlets.map((o) => <option key={o.code} value={o.code}>{o.name}</option>)}
          </select>
        </label>
        <label className="field small"><span>Status</span>
          <select data-testid="prd-status" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} style={inpStyle}>
            <option value="">All</option><option value="Partial">Partial</option><option value="Complete">Complete</option>
          </select>
        </label>
        <label className="field small"><span>Xero</span>
          <select data-testid="prd-xero" value={filters.xero_status} onChange={(e) => setFilters({ ...filters, xero_status: e.target.value })} style={inpStyle}>
            <option value="">All</option><option value="pending">Pending</option><option value="exported">Exported</option>
          </select>
        </label>
        <button data-testid="prd-apply" className="primary-button" onClick={applyFilters}><Search size={14} /> Apply</button>
      </div>

      <div className="report-summary" style={{ marginBottom: 12 }}>
        <div className="summary-tile"><span>GRNs</span><strong>{data.total}</strong></div>
        <div className="summary-tile"><span>Total received</span><strong className="teal">{money(data.grand_total)}</strong></div>
        <div className="summary-tile"><span>Pending Xero</span><strong className="amber">{data.pending_export}</strong></div>
        <div className="summary-tile"><span>Selected</span><strong data-testid="prd-selected-count">{selected.length}</strong></div>
      </div>

      {canExport && (
        <div className="action-cluster" style={{ marginBottom: 12 }}>
          <button data-testid="prd-validate" className="secondary-button" onClick={runValidate}><ShieldCheck size={15} /> Validate for Xero</button>
          <button data-testid="prd-export" className="primary-button" onClick={runExport}><Send size={15} /> Export for Xero (Bills)</button>
          <button data-testid="prd-unexport" className="secondary-button" onClick={markUnexported}><RotateCcw size={15} /> Mark un-exported</button>
        </div>
      )}

      <section className="panel">
        <PanelHead title="PO Received Detail" detail={loading ? "Loading..." : `${data.rows.length} of ${data.total} row(s)`} />
        {data.rows.length > 0 && (allMatchingSelected ? (
          <div data-testid="prd-selectall-bar" style={{ background: "#eef6f1", borderBottom: "1px solid var(--line)", padding: "8px 20px", fontSize: 13 }}>
            Semua <strong>{data.total}</strong> transaksi (semua halaman) terpilih.{" "}
            <button className="link-button" data-testid="prd-clear-selection" style={{ color: "var(--teal, #0a7)", fontWeight: 600, background: "none", border: "none", cursor: "pointer", padding: 0 }} onClick={() => setSelected([])}>Hapus pilihan</button>
          </div>
        ) : allOnPageSelected && data.total > data.rows.length ? (
          <div data-testid="prd-selectall-bar" style={{ background: "#fff7e6", borderBottom: "1px solid var(--line)", padding: "8px 20px", fontSize: 13 }}>
            <strong>{data.rows.length}</strong> transaksi di halaman ini terpilih.{" "}
            <button className="link-button" data-testid="prd-select-all-pages" style={{ color: "var(--teal, #0a7)", fontWeight: 600, background: "none", border: "none", cursor: "pointer", padding: 0 }} disabled={selectingAll} onClick={selectAllMatching}>
              {selectingAll ? "Memilih..." : `Pilih semua ${data.total} transaksi di semua halaman`}
            </button>
          </div>
        ) : null)}
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th style={{ width: 30 }}><input type="checkbox" data-testid="prd-select-all" checked={allOnPageSelected} onChange={toggleAll} /></th>
                <th style={{ width: 30 }}></th>
                <th>PO No.</th>
                <th>Vendor</th>
                <th>GRN</th>
                <th>Document</th>
                <th>Received</th>
                <th>Total</th>
                <th>Status</th>
                <th>Xero</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.length === 0 && <tr><td colSpan={10} style={{ textAlign: "center", padding: 40 }}>No records.</td></tr>}
              {data.rows.map((r) => (
                <React.Fragment key={r.id}>
                  <tr data-testid={`prd-row-${r.id}`}>
                    <td><input type="checkbox" data-testid={`prd-select-${r.id}`} checked={selected.includes(r.id)} onChange={() => toggleSel(r.id)} /></td>
                    <td>
                      <button className="icon-button" data-testid={`prd-expand-${r.id}`} onClick={() => setExpanded((p) => ({ ...p, [r.id]: !p[r.id] }))}>
                        {expanded[r.id] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </button>
                    </td>
                    <td><strong>{r.po_number}</strong></td>
                    <td>{r.supplier}</td>
                    <td>{r.grn_number}</td>
                    <td><strong>{r.document_number || "-"}</strong><small>{r.document_type}{r.document_date ? ` · ${r.document_date}` : ""}</small></td>
                    <td><small>{formatDate(r.received_at)}</small></td>
                    <td><strong>{money(r.total)}</strong></td>
                    <td><Badge tone={r.status === "Complete" ? "green" : "amber"}>{r.status}</Badge></td>
                    <td>{r.xero_exported ? <Badge tone="green">Exported</Badge> : <Badge tone="neutral">Pending</Badge>}</td>
                  </tr>
                  {expanded[r.id] && (
                    <tr className="prd-detail-row">
                      <td colSpan={10} style={{ background: "#fafcfa" }}>
                        <div style={{ padding: "6px 8px" }}>
                          <table>
                            <thead>
                              <tr>
                                <th>Item code</th><th>Item</th><th>UOM</th><th>Account</th>
                                <th>Ordered</th><th>Received</th><th>Outstanding</th>
                                <th>Unit price</th><th>Line total</th><th>Variance</th>
                              </tr>
                            </thead>
                            <tbody>
                              {r.lines.map((l, i) => (
                                <tr key={i} data-testid={`prd-line-${r.id}-${i}`}>
                                  <td>{l.sku || "-"}</td>
                                  <td><strong>{l.name}</strong></td>
                                  <td>{l.unit}</td>
                                  <td>{l.account_code ? <Badge tone="blue">{l.account_code}</Badge> : <small style={{ color: "var(--amber)" }}>Missing</small>}<small>{l.account_name}</small></td>
                                  <td>{l.ordered_qty}</td>
                                  <td><strong>{l.received_qty}</strong></td>
                                  <td>{l.outstanding_qty}</td>
                                  <td>{money(l.unit_price)}</td>
                                  <td><strong>{money(l.line_total)}</strong></td>
                                  <td>{l.price_variance ? <span className={l.price_variance > 0 ? "danger-text" : "success-text"}><strong>{money(l.price_variance)}</strong></span> : <small>-</small>}</td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr className="report-total-row"><td colSpan={8} style={{ textAlign: "right" }}>Grand total</td><td colSpan={2}><strong>{money(r.subtotal)}</strong></td></tr>
                            </tfoot>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 20px" }}>
          <button className="small-button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="prd-prev">Prev</button>
          <span style={{ fontSize: 12, color: "var(--muted)" }}>Page {data.page} / {totalPages}</span>
          <button className="small-button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="prd-next">Next</button>
        </div>
      </section>

      {validation && (
        <Modal title="Xero export validation" onClose={() => setValidation(null)}>
          <p className="form-hint">{validation.valid_count} valid · {validation.invalid_count} invalid. Invalid rows are skipped during export.</p>
          <div className="table-wrap" style={{ maxHeight: 400, overflow: "auto" }}>
            <table>
              <thead><tr><th>GRN</th><th>Document</th><th>Vendor</th><th>Result</th></tr></thead>
              <tbody>
                {validation.results.map((v) => (
                  <tr key={v.id} data-testid={`prd-valid-${v.id}`}>
                    <td><strong>{v.grn_number}</strong>{v.already_exported && <small>already exported</small>}</td>
                    <td>{v.document_number || "-"}</td>
                    <td>{v.supplier}</td>
                    <td>
                      {v.valid ? <Badge tone="green">Ready</Badge> : (
                        <div>{v.issues.map((it, i) => <div key={i}><Badge tone="amber">{it}</Badge></div>)}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="form-actions">
            <button className="secondary-button" onClick={() => setValidation(null)}>Close</button>
            {canExport && <button className="primary-button" data-testid="prd-export-from-modal" onClick={runExport}><Send size={15} /> Export valid rows</button>}
          </div>
        </Modal>
      )}
    </div>
  );
}
